import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = "/logo512.png",
  url = "https://finfratech.com",
  type = "website",
  structuredData
}) => {
  const { i18n } = useTranslation();
  
  // Default content based on language
  const defaultContent = {
    en: {
      title: "FinfraTech | Software Development & Cloud Solutions",
      description: "FinfraTech delivers professional software development and cloud solutions. AWS certified full-stack developer specializing in custom software, cloud infrastructure, and WordPress development. Expert in React, Node.js, PHP, Laravel, and modern web technologies.",
      keywords: "finfratech, software development, cloud solutions, AWS certified, full-stack developer, custom software, cloud infrastructure, WordPress development, React, Node.js, PHP, Laravel, TypeScript, microservices, CI/CD, web development, Estonia"
    },
    et: {
      title: "FinfraTech - Tarkvaraarendus ja pilve lahendused | AWS sertifitseeritud arendaja",
      description: "FinfraTech pakub professionaalset tarkvaraarendust ja pilve lahendusi. AWS sertifitseeritud full-stack arendaja, spetsialiseerub kohandatud tarkvarale, pilveinfrastruktuurile ja WordPress arendusele. Ekspert React, Node.js, PHP, Laravel ja kaasaegsetes veebitehnoloogiates.",
      keywords: "finfratech, tarkvaraarendus, pilve lahendused, AWS sertifitseeritud, full-stack arendaja, kohandatud tarkvara, pilveinfrastruktuur, WordPress arendus, React, Node.js, PHP, Laravel, TypeScript, mikroteenused, CI/CD, veebiarendus, Eesti"
    }
  };

  const currentLang = i18n.language as 'en' | 'et';
  const langContent = defaultContent[currentLang] || defaultContent.en;
  
  const finalTitle = title || langContent.title;
  const finalDescription = description || langContent.description;
  const finalKeywords = keywords || langContent.keywords;
  const fullImageUrl = image.startsWith('http') ? image : `${url}${image}`;
  
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FinfraTech",
    "alternateName": currentLang === 'et' ? "FinfraTech Tarkvaraarendus" : "FinfraTech Software Development",
    "description": finalDescription,
    "url": url,
    "logo": fullImageUrl,
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": currentLang === 'et' ? "Estonian" : "English"
    },
    "sameAs": [],
    "foundingDate": "2022",
    "numberOfEmployees": "1-10",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "EE",
      "addressLocality": "Tallinn"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "59.4370",
        "longitude": "24.7536"
      },
      "geoRadius": "5000000"
    },
    "knowsAbout": currentLang === 'et' ? [
      "tarkvaraarendus",
      "pilve lahendused",
      "AWS",
      "React",
      "Node.js",
      "PHP",
      "Laravel",
      "WordPress arendus",
      "TypeScript",
      "mikroteenused"
    ] : [
      "software development",
      "cloud solutions",
      "AWS",
      "React",
      "Node.js",
      "PHP",
      "Laravel",
      "WordPress development",
      "TypeScript",
      "microservices"
    ],
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": currentLang === 'et' ? "Tarkvaraarendus" : "Software Development",
          "description": currentLang === 'et' ? "Kohandatud tarkvaralahendused, mis on kavandatud ja loodud teie äriprobleemide lahendamiseks." : "Custom software solutions, designed and engineered to solve your business problems."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": currentLang === 'et' ? "Pilve lahendused" : "Cloud Solutions",
          "description": currentLang === 'et' ? "Skaleeritav pilveinfrastruktuur ja juurutamise lahendused teie rakenduste jaoks." : "Scalable cloud infrastructure and deployment solutions for your applications."
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="FinfraTech" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={currentLang === 'et' ? 'Estonian' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="FinfraTech" />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content="FinfraTech Logo" />
      <meta property="og:locale" content={currentLang === 'et' ? 'et_EE' : 'en_US'} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content="FinfraTech Logo" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="EE" />
      <meta name="geo.placename" content="Estonia" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
